import React from 'react'

type KeyEventHandlers = {
  keyName: string
  keyHandler: (e: KeyboardEvent) => void
  ctrlKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  metaKey?: boolean
  ignoreEditableTarget?: boolean
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false

  return Boolean(
    target.closest(
      [
        'input',
        'textarea',
        'select',
        '[contenteditable="true"]',
        '[role="textbox"]',
        '[role="combobox"]',
      ].join(',')
    ) || target.isContentEditable
  )
}

function useKeyboardEventListeners(
  keyHandlers: KeyEventHandlers[],
  debug = false
) {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { key, ctrlKey, shiftKey, altKey, metaKey } = event

      debug &&
        console.log('keyName is', key, { ctrlKey, shiftKey, altKey, metaKey })

      keyHandlers.forEach(handler => {
        const {
          keyName,
          keyHandler,
          ctrlKey: requiredCtrl = false,
          shiftKey: requiredShift = false,
          altKey: requiredAlt = false,
          metaKey: requiredMeta = false,
          ignoreEditableTarget = false,
        } = handler

        // Check if key matches
        if (key !== keyName) return
        if (ignoreEditableTarget && isEditableTarget(event.target)) return

        // Check if all required modifier keys match
        if (
          ctrlKey === requiredCtrl &&
          shiftKey === requiredShift &&
          altKey === requiredAlt &&
          metaKey === requiredMeta
        ) {
          keyHandler(event)
        }
      })
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [keyHandlers, debug])
}

export { useKeyboardEventListeners }
